export type PropertySetupProperty = {
  id?: number;
  name?: string | null;
  address?: string | null;
  managementType?: string | null;
  status?: string | null;
  roomCount?: number | string | null;
  bathroomCount?: number | string | null;
  description?: string | null;
  wifiName?: string | null;
  wifiPassword?: string | null;
  trashPickupDay?: string | null;
  knowledgeBase?: string | null;
};

export type PropertySetupRoom = {
  id: number;
  roomType?: string | null;
  roomTitle?: string | null;
  shadowOfRoomId?: number | null;
  associatedBedroomIds?: number[] | null;
  inputImageUrl?: string | null;
  outputImageUrl?: string | null;
  hasSavedImages?: boolean;
};

export type PropertySetupCoreChecks = {
  basicDetails: boolean;
  propertyPhoto: boolean;
  bedrooms: boolean;
  bedroomPhotos: boolean;
  wifi: boolean;
  wastePickup: boolean;
  knowledgeBase: boolean;
};

export const PROPERTY_SETUP_CORE_TOTAL = 7;

export function isUsefulPropertyKnowledgeBase(content: string | null | undefined): boolean {
  const value = (content ?? "").split("\n").filter((line) => {
    const text = line.trim();
    return text && !text.startsWith("#") &&
      !/\[[^\]]*\]/.test(text.replace(/\[([^\]]+)\]\(https?:\/\/[^)]+\)/g, "$1")) &&
      !/^-?\s*No smoking inside the property$/i.test(text) &&
      !/^-?\s*Quiet hours: 10pm - 8am$/i.test(text);
  }).join("\n").trim();
  return value.length >= 20;
}

export function propertySetupCoreChecks(
  property: PropertySetupProperty,
  propertyPhoto: string | null | undefined,
  savedRooms: readonly PropertySetupRoom[] | null | undefined,
): PropertySetupCoreChecks {
  const rooms = (savedRooms ?? []).filter((room) => !room.shadowOfRoomId);
  const bedrooms = rooms.filter((room) => room.roomType === "bedroom");
  const bathrooms = rooms.filter((room) => room.roomType === "bathroom");
  const bathroomBedroomIds = new Set(bathrooms.flatMap((room) => room.associatedBedroomIds ?? []));
  return {
    basicDetails: Boolean(property.name?.trim() && property.address?.trim() &&
      property.managementType && property.status && Number(property.roomCount) > 0 &&
      Number(property.bathroomCount) > 0 && property.description?.trim()),
    propertyPhoto: Boolean(propertyPhoto),
    bedrooms: bedrooms.length > 0 && bedrooms.every((room) =>
      Boolean(room.roomTitle?.trim()) &&
      (bathrooms.length === 0 || bathroomBedroomIds.has(room.id))),
    bedroomPhotos: bedrooms.length > 0 && bedrooms.every((room) =>
      Boolean(room.hasSavedImages || room.inputImageUrl || room.outputImageUrl)),
    wifi: Boolean(property.wifiName?.trim() && property.wifiPassword?.trim()),
    wastePickup: Boolean(property.trashPickupDay?.trim()),
    knowledgeBase: isUsefulPropertyKnowledgeBase(property.knowledgeBase),
  };
}

export function propertySetupCoreProgress(
  property: PropertySetupProperty,
  propertyPhoto: string | null | undefined,
  rooms: readonly PropertySetupRoom[] | null | undefined,
) {
  const checks = propertySetupCoreChecks(property, propertyPhoto, rooms);
  const completed = Object.values(checks).filter(Boolean).length;
  return {
    completed,
    total: PROPERTY_SETUP_CORE_TOTAL,
    percent: Math.round((completed / PROPERTY_SETUP_CORE_TOTAL) * 100),
  };
}
